'use client';

import { motion } from 'motion/react';
import { useRef } from 'react';

import Logo from '~/assets/vector/logo.svg';
import Heading from '~/components/atoms/Heading';
import Media from '~/components/molecules/Media';
import WysiwygString from '~/components/molecules/WysiwygString';
import { schemas } from '~/components/organisms/slices/content-blocks/@types/schemas';
import { typenames } from '~/components/organisms/slices/content-blocks/@types/typenames';
import { SliceProps } from '~/lib/@types/slice-component';
import { useLenisElementPosition } from '~/lib/hooks/useLenisElementPosition';
import { cn } from '~/lib/utils/classname';
import { hasMedia } from '~/lib/utils/media';

interface Props {
  sliceData: SliceProps<
    typeof typenames.homepageHeaderLayout,
    typeof schemas.homepageHeaderLayout
  >;
}

const HomepageHeaderContents: React.FC<Props> = ({ sliceData }) => {
  const { mainMedia, secondaryMedia, tagline, secondaryTitle } =
    schemas.homepageHeaderLayout.parse(sliceData);

  const hasMainMedia = mainMedia?.media && hasMedia(mainMedia.media);

  const hasSecondaryMedia =
    secondaryMedia?.media && hasMedia(secondaryMedia.media);

  const ref = useRef<HTMLDivElement>(null);
  const y = useLenisElementPosition(ref, {
    mode: 'percent',
    absolute: true,
    bounded: true,
  });

  return (
    <header
      className="relative isolate flex justify-center overflow-hidden"
      data-slice={typenames.homepageHeaderLayout}
    >
      <div className="pointer-events-none absolute top-0 left-0 h-full w-full"></div>

      <div className="relative flex w-full justify-center" ref={ref}>
        <motion.div
          className="relative w-full origin-top overflow-clip after:absolute after:top-0 after:left-0 after:z-10 after:h-full after:w-full after:bg-black/20"
          style={{
            scale: `${100 - 15 * y}%`,
            opacity: `${1 - 0.5 * y}`,
          }}
        >
          <div className="bg-brand-secondary relative grid h-[max(480px,100svh)] grid-cols-2">
            {hasMainMedia && (
              <div
                className={cn('relative h-full', {
                  'col-span-2': !hasSecondaryMedia,
                  'col-span-1': hasSecondaryMedia,
                })}
              >
                <Media
                  media={mainMedia.media}
                  imageProps={{
                    className: 'h-full w-full object-cover',
                    fill: true,
                    priority: true,
                  }}
                  videoProps={{
                    className: 'absolute h-full w-full object-cover',
                    loop: true,
                    autoPlay: true,
                    playsInline: true,
                    muted: true,
                  }}
                  reactPlayerProps={{
                    className: '!w-full !h-full !object-cover',
                    playing: true,
                    playsInline: true,
                    loop: true,
                    muted: true,
                  }}
                />
              </div>
            )}

            {hasSecondaryMedia && (
              <div className="relative col-span-1">
                <Media
                  media={secondaryMedia.media}
                  imageProps={{
                    className: 'h-full w-full object-cover',
                    fill: true,
                    priority: true,
                  }}
                  videoProps={{
                    className: 'h-full w-full object-cover',
                    loop: true,
                    autoPlay: true,
                    playsInline: true,
                    muted: true,
                  }}
                  reactPlayerProps={{
                    className: '!w-full !h-full !object-cover',
                    playing: true,
                    playsInline: true,
                    loop: true,
                    muted: true,
                  }}
                />
              </div>
            )}
          </div>

          {(tagline || secondaryTitle) && (
            <div className="absolute top-0 left-0 z-20 flex h-full w-full flex-col items-center justify-center gap-y-2">
              {tagline && (
                <Heading
                  as="h2"
                  level={3}
                  html={tagline}
                  className="relative flex flex-col items-center text-center font-normal text-balance text-white"
                />
              )}

              {secondaryTitle && (
                <div
                  className={cn(
                    'text-2xs relative flex flex-col items-center text-center font-normal text-balance',
                    {
                      'text-brand-quinary': !hasMainMedia && !hasSecondaryMedia,
                      'text-brand-tertiary': hasMainMedia || hasSecondaryMedia,
                    }
                  )}
                >
                  <WysiwygString html={secondaryTitle} />
                </div>
              )}
            </div>
          )}

          <div className="pointer-events-none absolute bottom-16 left-0 flex w-full items-center justify-center text-white">
            <Logo className="h-6.5" />
          </div>
        </motion.div>
      </div>
    </header>
  );
};

export default HomepageHeaderContents;
